import { cva } from "class-variance-authority";
import { cn } from "../../lib/cn";

/**
 * Phase 2 primitive — `<Chip>` replaces `.chip`, `.bwarn`, `.bcrit`, the
 * various `kb-chip` / `aud-chip` tone variants, and mention/pill badges
 * scattered across the app. The `dot` slot is reserved for a coloured
 * leading indicator (used by `.chip .dot` in common-ui.css).
 *
 * The `size` variants match the two densities in use today — pill-sized
 * (standard, 9px 3px padding, 10px text) and tight-sized (for drawer
 * metadata rows where vertical space is tight).
 */
const chip = cva(
  "inline-flex items-center gap-1 rounded-full font-bold uppercase whitespace-nowrap",
  {
    variants: {
      size: {
        sm: "px-1.5 py-px text-[9px] tracking-[0.04em]",
        md: "px-[9px] py-[3px] text-[10px] tracking-[0.06em]",
        lg: "px-3 py-1 text-[11px] tracking-[0.05em]",
      },
      tone: {
        default: "bg-[var(--rc-surface-hover)] border border-rc-border text-rc-text-secondary",
        info:    "bg-[rgba(59,127,250,0.12)] border border-[rgba(59,127,250,0.24)] text-rc-accent-strong",
        success: "bg-[rgba(16,201,129,0.12)] border border-[rgba(16,201,129,0.24)] text-[#10c981]",
        warn:    "bg-[rgba(245,158,11,0.1)] border border-[rgba(245,158,11,0.24)] text-rc-yellow",
        crit:    "bg-[rgba(239,68,68,0.1)] border border-[rgba(240,68,68,0.24)] text-rc-red",
        plan:    "bg-[rgba(168,85,247,0.12)] border border-[rgba(168,85,247,0.24)] text-[#c084fc]",
        muted:   "bg-[rgba(107,107,107,0.12)] border border-rc-border text-[#6b7280]",
      },
    },
    defaultVariants: { size: "md", tone: "default" },
  },
);

export default function Chip({ children, size, tone, dot, className, ...props }) {
  return (
    <span className={cn(chip({ size, tone }), "border", className)} {...props}>
      {dot && (
        <span
          className="inline-block w-[5px] h-[5px] rounded-full shrink-0"
          style={{ background: dot }}
        />
      )}
      {children}
    </span>
  );
}
